import React, { createContext, useContext, useState, useMemo } from 'react';
import { mockConsumerUnits } from '../utils/mockData';

const ConsumerUnitContext = createContext(null);

/**
 * Global unit selection context for the Manager role.
 * Wraps AppShell so the Header and all manager pages share the same unit scope.
 *
 * - fixedUnitId: locks to a single unit (Director drill-down)
 * - If only 1 real unit exists, defaults to that unit (no "All Units" option)
 */
function ConsumerUnitProvider({ children, fixedUnitId }) {
  const allUnits = mockConsumerUnits;
  const realUnits = allUnits.filter((u) => u.id !== 'all');
  const singleUnit = realUnits.length === 1;
  const defaultId = fixedUnitId || (singleUnit ? realUnits[0].id : 'all');

  const [selectedUnitId, setSelectedUnitId] = useState(defaultId);
  const activeUnitId = fixedUnitId || selectedUnitId;

  const selectedUnit = useMemo(
    () => allUnits.find((u) => u.id === activeUnitId) || allUnits[0],
    [allUnits, activeUnitId],
  );

  const value = useMemo(
    () => ({
      units: singleUnit ? realUnits : allUnits,
      selectedUnitId: activeUnitId,
      setSelectedUnitId: fixedUnitId ? () => {} : setSelectedUnitId,
      selectedUnit,
      isFixed: !!fixedUnitId,
      singleUnit,
    }),
    [allUnits, realUnits, activeUnitId, fixedUnitId, selectedUnit, singleUnit, setSelectedUnitId],
  );

  return (
    <ConsumerUnitContext.Provider value={value}>
      {children}
    </ConsumerUnitContext.Provider>
  );
}

function useConsumerUnit() {
  const ctx = useContext(ConsumerUnitContext);
  if (!ctx) throw new Error('useConsumerUnit must be used within ConsumerUnitProvider');
  return ctx;
}

export { ConsumerUnitProvider, useConsumerUnit };
